import { Email } from "@/Common/Api/Contacts/Dto/Email";
import { OrphanLabForwardDTO } from "@/Common/Api/Labs/Dto/Responses/LabMessageGroupDTO";
import { PdfTag } from "@/Common/Api/RadPdf/Dto/Responses/PdfTag";
import { ArrayHelper } from "@/Common/Helpers/ArrayHelper";
import { RAMQPatient } from "@/Common/Models/RAMQ/RAMQPatient";
import { KeyValuePair } from "@/Common/Models/UI/KeyValuePair";
import * as LocalOperations from "@/Common/Operations/operations.local";
import * as ServerOperations from "@/Common/Operations/operations.server";
import { BaseAction } from "@/Common/Stores/Actions/BaseAction";
import { LocalEvent } from "@/Common/Stores/Actions/LocalEvent";
import { ServerEvent } from "@/Common/Stores/Actions/ServerEvent";
import { Dispatcher } from "@/Common/Stores/Dispatcher";
import { BaseServerOperationHandler } from "@/Common/Stores/Handlers/Server/BaseServerOperationHandler";
import { CertThumbprint } from "@/Settings/Api/Dto/Response/CertThumbprint";
import { OrphanLabsClinician } from "@/Settings/Api/Dto/Response/OrphanLabsClinician";
import { SharingSettings } from "@/Settings/Api/Dto/Response/SharingSettings";
import { SettingsStore } from "@/Settings/Stores/SettingsStore";

class SettingsResponseHandler extends BaseServerOperationHandler {
    constructor(action: BaseAction) {
        super(action);

        this.RegisterOperation(ServerOperations.Server.Configuration_GetEmployeeSharing, this.GetEmployeeSharing);
        this.RegisterOperation(ServerOperations.Server.ARLMYLE_RemoveClinicianFromOrphanLabForwards, this.OrphanLabHadler);
        this.RegisterOperation(ServerOperations.Server.ARLMYLE_SaveOrphanLabForwards, this.OrphanLabHadler);
        this.RegisterOperation(ServerOperations.Server.ARLMYLE_GetOrphanLabForwards, this.GetOrphanLabForwards);
        this.RegisterOperation(ServerOperations.Server.LoadPDFTags, this.LoadPDFTags);
        this.RegisterOperation(ServerOperations.Server.RAMQ_GetPatientsRegistration, this.LoadPatientRegistration);
        this.RegisterOperation(ServerOperations.Server.Configuration_ChangeLanguage, this.ChangedLanguage);
        this.RegisterOperation(ServerOperations.Server.Configuration_ChangeUnit, this.ChangedUnit);
        this.RegisterOperation(ServerOperations.Server.MSA_LocateCertificateKeys, this.LocateCertificateKeys);
        this.RegisterOperation(ServerOperations.Server.Configuration_UpdateEmployeeThumbprintDSQ, this.UpdateEmployeeThumbprintDSQ);
        this.RegisterOperation(ServerOperations.Server.Myle_Settings_SaveSignFileName, this.SaveSignatureImage);
        this.RegisterOperation(ServerOperations.Server.Myle_Settings_LoadSignatureByteArray, this.LoadSignatureImage);
        this.RegisterOperation(ServerOperations.Server.Myle_ChangeEmployeeSite, this.changeEmployeeSite);
        this.RegisterOperation(ServerOperations.Server.Myle_Employee_Get_Emails, this.HandleLoadEmployeeEmailsResponse);
        this.RegisterOperation(ServerOperations.Server.Myle_Employee_Save_Emails, this.HandleEmailsSavedResponse);
        this.RegisterOperation(ServerOperations.Server.Myle_Verify_Email, this.HandleVerifyEmailResponse);
        this.RegisterOperation(ServerOperations.Server.Myle_Employee_Resend_Verification_Code, this.HandleResendEmailVerificationCodeResponse);
        this.RegisterOperation(ServerOperations.Server.Configuration_UpdateActiveDepartment, this.changeActiveDepartment);
        this.RegisterOperation(ServerOperations.Server.Myle_Settings_SavePreferedFaxNumber, this.updateSavePrefereredFaxNumber);
        this.RegisterOperation(ServerOperations.Server.Myle_MultifactorAuthentication_AuthenticatorCodeDeliveryMethod, this.updateAuthenticatorCodeDeliveryMethod);
    }

    private UpdateEmployeeThumbprintDSQ(response: ServerEvent): void {
        const ThumbPrintDSQ: string = response.Data.ThumbPrintDSQ;
        const Success: boolean = response.Data.Success;

        if (Success) {
            cacheStore.GetCurrentEmployee().ThumbprintKeyDsq = ThumbPrintDSQ;
            sessionStore.clearCurrentEmployeeCache();
            settingsStore.uiLogic = settingsStore.uiLogic.set("DisplayLocatedDSQCertificates", false);
        }
    }

    private LocateCertificateKeys(response: ServerEvent, store: SettingsStore): void {
        const certificates: CertThumbprint[] = response.Data.CertificateThumbprints;

        if (certificates != null) {
            store.uiLogic = store.uiLogic.set("LocatedDSQCertificates", certificates);
            store.uiLogic = store.uiLogic.set("DisplayLocatedDSQCertificates", true);
        }
        else if (response.Errors && response.Errors.length > 0) {
            sessionStore.showPopup(response.Errors);
        }
    }

    private ChangedLanguage(response: ServerEvent, store: SettingsStore): void {
        if (response.hasErrors())
            return;

        const languageCode = response.Data.NewLanguageCode;
        const lookupId = response.Data.PreferredOfficialLanguageLookupId;

        store.configuration = store.configuration.set("Language", languageCode);
        store.ResetUnitOptions();
        store.resetDocumentView();
        // update specialties (translate names)
        administrationStore.updateServiceSearchSpecializations();

        sessionStore.updateCurrentEmployee(p => p.PreferredOfficialLanguageLookupId = lookupId);
    }

    private ChangedUnit(response: ServerEvent, store: SettingsStore): void {
        const useMetric = response.Data["UseMetricSystem"];

        store.configuration = store.configuration.set("DefaultUnit", useMetric ? "metric" : "imperial");
    }

    private SaveSignatureImage(response: ServerEvent, store: SettingsStore): void {
        if (response.Data.Success == true) {
            store.configuration = store.configuration.set("SignatureByteArray", response.Data.SignatureBase64);
            sessionStore.signOnPrintAccess = response.Data.SignatureBase64 != "" && sessionStore.hasPinSet == true;
        }
    }

    private LoadSignatureImage(response: ServerEvent, store: SettingsStore): void {
        if (response.Data.Success == true) {
            store.configuration = store.configuration.set("SignatureByteArray", response.Data.SignatureBase64);
        }
    }

    private GetEmployeeSharing(response: ServerEvent, store: SettingsStore): void {
        let sharing: SharingSettings = response.Data;
        let taskClinicians = cacheStore.Clinicians.filter(c => {
            const i = sharing.TaskClinicians.filter(z => z === c.ContactId).length;
            return i > 0;
        });

        let msgClinicians = cacheStore.Clinicians.filter(c => {
            const i = sharing.MessageClinicians.filter(z => z === c.ContactId).length;
            return i > 0;
        });

        let xcomClinicians = cacheStore.Clinicians.filter(c => {
            const i = sharing.ExternalCommunicationsClinicians.filter(z => z === c.ContactId).length;
            return i > 0;
        });

        const portalCommunicationSharedEmployees = cacheStore
            .CliniciansAndEmployeeGroups
            .filter(
                clinician => sharing
                    .PortalCommunicationsClinicians
                    .filter(id => id === clinician.ContactId)
                    .length
            );

        store.configuration = store.configuration.set("CliniciansForTasks", Immutable.List(taskClinicians));
        store.configuration = store.configuration.set("CliniciansForMessages", Immutable.List(msgClinicians));
        store.configuration = store.configuration.set("CliniciansForExternalCommunications", Immutable.List(xcomClinicians));
        store.configuration = store.configuration.set("CliniciansForPortalCommunications", Immutable.List(portalCommunicationSharedEmployees));

        store.action.LoadPdfTagsCache();
    }

    private OrphanLabHadler(): void {
        window.setTimeout(
            () => {
                // reload list
                const operation = new LocalEvent(LocalOperations.Settings.LoadOrphanLabsForwards);
                LocalEventDispatcher.dispatch(operation);
            },
            0
        );
    }

    private GetOrphanLabForwards(response: ServerEvent, store: SettingsStore): void {
        let orphanLabs: OrphanLabForwardDTO[] = response.Data.OrphanLabForwardDTOs;

        if (orphanLabs) {
            const list = orphanLabs.map(
                p => new OrphanLabsClinician({
                    Id: p.ID,
                    DateFrom: p.FromDate,
                    DateTo: p.ToDate,
                    LicenceNumber: p.ForwardedTo,
                    Name: p.ForwardedToName
                })
            );

            store.configuration = store.configuration.set("ClinicianListForOrphanLabs", Immutable.List(list));
        }
        else {
            store.configuration = store.configuration.set("ClinicianListForOrphanLabs", Immutable.List([]));
        }
    }

    private LoadPDFTags(response: ServerEvent, store: SettingsStore): void {
        let tags: PdfTag[] = [];
        tags = response.Data.Tags;
        const kvpList = tags.map(tag => new KeyValuePair(tag.Id, tag.Tag));
        store.configuration = store.configuration.set("PdfTagsCache", Immutable.List(kvpList));
        store.uiLogic = store.uiLogic.set("LoadingTagsCache", false);
        const currentTagNames = store.configuration.get("PDFSearchKeywords").toArray();
        // here we find tags by name (its value) and convert to id for internal use
        const currentTagIds = kvpList.filter(kvp => currentTagNames.indexOf(kvp.Value) !== -1).map(d => d.Key);
        store.configuration = store.configuration.set("SelectePDFTagIds", Immutable.List(currentTagIds));
    }

    private LoadPatientRegistration(response: ServerEvent, store: SettingsStore): void {
        let patientList: RAMQPatient[] = response.Data["RAMQPatientList"];
        patientList = ArrayHelper.SortDynamic(patientList, "LastName", false);
        store.configuration = store.configuration.set("RAMQPatientList", Immutable.List(patientList));
    }

    private changeEmployeeSite(response: ServerEvent, store: SettingsStore): void {
        if (response.Data["IsSuccess"]) {
            sessionStore.getCurrentEmployee().Model.ActiveDepartmentId = null;
            store.configuration = store.configuration.set("ActiveDepartment", null);
            Dispatcher.dispatch(LocalOperations.Sites.SetActiveSite, { ActiveSiteId: response.Data["ActiveSiteId"] }, 0);
        }
    }

    private HandleLoadEmployeeEmailsResponse(response: ServerEvent, store: SettingsStore): void {
        const emails: Email[] = response.Data.Emails;
        const isNotifiedByEmail: boolean = response.Data.IsNotifiedByEmail;
        const lastVerifiedEmail: string = response.Data.LastVerifiedEmail;
        const contactPointLookups = cacheStore.GetContactPointTypeLookups();

        if (emails)
            emails.forEach(x => {
                x.AvailableContactPointTypeLookupIds = contactPointLookups;
                x.IsValid = Email.validateEmail(x.Address);
                x.renderId = sessionStore.newId().toString();
            });

        const emailAddressesModel = {
            Emails: Immutable.List(emails),
            IsNotifiedByEmail: isNotifiedByEmail
        };
        store.configuration = store.configuration
            .set("EmailAddressesModel", Immutable.fromJS(emailAddressesModel))
            .set("AddEmailsErrorMessage", "")
            .set("EmailVerificationErrorMessage", "")
            .set("LastVerifiedEmail", lastVerifiedEmail);

        store.uiLogic = store.uiLogic.set("ShowEditEmailAddresses", false);
    }

    private HandleEmailsSavedResponse(response: ServerEvent, store: SettingsStore): void {
        store.uiLogic = store.uiLogic.set("PwdConfirmationInProgress", false);

        if (response.Errors != null && response.Errors.length > 0) {
            const error = response.Errors.firstOrDefault().Message;
            store.configuration = store.configuration.set("AddEmailsErrorMessage", error);
            return;
        }

        window.setTimeout(
            () => LocalEventDispatcher.dispatch(new LocalEvent(LocalOperations.Settings.LoadEmployeeEmails)),
            0
        );
    }

    private HandleVerifyEmailResponse(response: ServerEvent, store: SettingsStore): void {
        store.uiLogic = store.uiLogic.set("EmailVerificationInProcess", false);

        if (response.Errors != null && response.Errors.length > 0) {
            let error = response.Errors.firstOrDefault().Code;
            store.configuration = store.configuration.set("EmailVerificationErrorMessage", error);
            return;
        }

        window.setTimeout(
            () => LocalEventDispatcher.dispatch(new LocalEvent(LocalOperations.Settings.LoadEmployeeEmails)),
            0
        );
    }

    private HandleResendEmailVerificationCodeResponse(response: ServerEvent, store: SettingsStore): void {
        store.uiLogic = store.uiLogic.set("EmailVerificationInProcess", false);

        if (response.Errors != null && response.Errors.length > 0) {
            let error = response.Errors.firstOrDefault().Code;
            store.configuration = store.configuration.set("EmailVerificationErrorMessage", error);
        }
    }

    private changeActiveDepartment(response: ServerEvent, store: SettingsStore): void {
        if (response.Data["IsSuccess"]) {
            let activeDepartmentId = response.Data["ActiveDepartmentId"];
            store.configuration = store.configuration.set("ActiveDepartment", activeDepartmentId);
            let costCenters = cacheStore.getCostCenterBySiteId(store.configuration.get("ActiveSite"));
            store.configuration = store.configuration.set("CostCentersList", costCenters);
            sessionStore.getCurrentEmployee().Model.ActiveDepartmentId = activeDepartmentId;
        }
    }

    private updateSavePrefereredFaxNumber(response: ServerEvent): void {
        if (response.Errors?.any())
            return;

        const faxAccountNumberId = response.Data["FaxAccountNumberId"];
        sessionStore.getCurrentEmployee().PreferedFaxAccountNumberId = faxAccountNumberId;
    }

    private updateAuthenticatorCodeDeliveryMethod(response: ServerEvent, store: SettingsStore): void {
        if (response.Errors?.any())
            return;

        const method = response.Data["AuthenticatorCodeDeliveryMethod"];
        store.configuration = store.configuration.set("SelectedAuthenticatorCodeDeliveryMethod", method);
    }
}

export { SettingsResponseHandler };
